import { useState } from "react";
import { X, Plus, Trash2, ListPlus, CheckSquare, Package, AlertTriangle, ArrowUp, ArrowDown, Pencil, Check as CheckIcon, Cpu, Ruler } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ManageUnitsDialog({ report, onClose, onChanged, mode = "all", initialTab: initialTabProp = null, initialKind = null }) {
  const [busy, setBusy] = useState(false);
  // mode:
  //   "units-only" → vain yksiköiden hallinta (lisää/poista/nimeä)
  //   "content"    → vain raportin sisältö (tarkistuslistat + tarvikkeet)
  //   "all"        → kaikki välilehdet (legacy backward compat)
  //
  // initialKind: "work" | "findings" | null
  //   Kun asetettu, "checklist"-välilehti näyttää vain valitun tyypin
  //   editorin (ei kind-radio-valintaa). Käytetään sisällön kohdennukseen.
  const initialTab = initialTabProp || (mode === "content" ? "checklist" : "units");
  const [tab, setTab] = useState(initialTab);
  const kindLocked = initialKind === "work" || initialKind === "findings";

  // Units state
  const [bulkText, setBulkText] = useState("");
  const [newLabel, setNewLabel] = useState("");

  // Checklist broadcast state
  const [clText, setClText] = useState("");
  const [clKind, setClKind] = useState(initialKind || "normal"); // "normal" | "findings"
  // Per-unit selection for the checklist broadcast — empty Set means "all".
  // Default behavior preserves backward compatibility (apply to all).
  const [clSelectedUnits, setClSelectedUnits] = useState(() => new Set((report?.units || []).map((u) => u.id)));

  // Materials append state
  const [matName, setMatName] = useState("");
  const [matUnit, setMatUnit] = useState("kpl");

  const addOne = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${report.report_id}/units`, { label: newLabel.trim() });
      onChanged(data);
      setNewLabel("");
      toast.success("Yksikkö lisätty");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lisäys epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const addBulk = async () => {
    const lines = bulkText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!lines.length) return;
    setBusy(true);
    try {
      let last = report;
      for (const lbl of lines) {
        const { data } = await api.post(`/reports/${report.report_id}/units`, { label: lbl });
        last = data;
      }
      onChanged(last);
      setBulkText("");
      toast.success(`${lines.length} yksikkö${lines.length !== 1 ? "ä" : ""} lisätty`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lisäys epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const removeUnit = async (unit_id) => {
    if (!confirm("Poistetaanko yksikkö ja sen kuvat pysyvästi?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/reports/${report.report_id}/units/${unit_id}`);
      onChanged(data);
      toast.success("Yksikkö poistettu");
    } catch (e) {
      toast.error("Poisto epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  // Reorder a unit one step up/down. Uses the existing PATCH /reports/{id}
  // endpoint with the full units array in the new order.
  const moveUnit = async (unit_id, direction) => {
    const units = [...(report.units || [])];
    const idx = units.findIndex((u) => u.id === unit_id);
    if (idx < 0) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= units.length) return;
    [units[idx], units[target]] = [units[target], units[idx]];
    setBusy(true);
    try {
      const { data } = await api.patch(`/reports/${report.report_id}`, { units });
      onChanged(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Järjestyksen muutos epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const broadcastChecklist = async () => {
    const labels = clText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!labels.length) return;
    const allUnitIds = (report.units || []).map((u) => u.id);
    const selectedIds = allUnitIds.filter((id) => clSelectedUnits.has(id));
    if (selectedIds.length === 0) {
      toast.error("Valitse vähintään yksi yksikkö");
      return;
    }
    setBusy(true);
    try {
      // If user selected EVERY unit, send empty unit_ids (== "all") to keep
      // the payload small. Otherwise send the selected ids subset.
      const body = {
        labels,
        is_finding: clKind === "findings",
        unit_ids: selectedIds.length === allUnitIds.length ? [] : selectedIds,
      };
      const { data } = await api.post(`/reports/${report.report_id}/units/checklist-broadcast`, body);
      onChanged(data.report);
      setClText("");
      const added = data.added || 0;
      const tail = selectedIds.length === allUnitIds.length ? "kaikille yksiköille" : `${selectedIds.length} yksikölle`;
      if (added > 0) {
        toast.success(`Lisätty ${added} ${clKind === "findings" ? "puute-kohta" : "tarkistuskohta"}${added !== 1 ? "a" : ""} ${tail}`);
      } else {
        toast.info("Kaikki kohdat olivat jo olemassa");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lisäys epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const toggleUnit = (uid) => {
    setClSelectedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const toggleAllUnits = () => {
    const allUnitIds = (report.units || []).map((u) => u.id);
    setClSelectedUnits((prev) => prev.size === allUnitIds.length ? new Set() : new Set(allUnitIds));
  };

  const appendMaterial = async () => {
    const name = matName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${report.report_id}/materials`, {
        materials: [{ name, unit: (matUnit || "kpl").trim() || "kpl" }],
      });
      onChanged(data.report);
      setMatName("");
      if (data.added > 0) {
        toast.success("Tarvike lisätty raportille");
      } else {
        toast.info("Tarvike on jo raportilla");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lisäys epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const removeMaterial = async (name) => {
    const rest = (report.materials || []).filter((m) => m.name !== name);
    setBusy(true);
    try {
      const { data } = await api.patch(`/reports/${report.report_id}`, { materials: rest });
      onChanged(data);
      toast.success("Tarvike poistettu");
    } catch (e) {
      toast.error("Poisto epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (id, label, Icon) => (
    <button
      onClick={() => setTab(id)}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 transition-colors ${
        tab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
      data-testid={`manage-tab-${id}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="manage-units-dialog" onClick={onClose}>
      <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold">
              {mode === "content"
                ? (initialKind === "findings" ? "Havaintolistat" : initialKind === "work" ? "Työvaiheet" : initialTabProp === "materials" ? "Vakiotarvikkeet" : "Raportin sisältö")
                : mode === "units-only" ? "Yksiköt" : "Hallinnoi raporttia"}
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {mode === "content"
                ? (initialKind === "findings" ? "Havaintolistat kentälle" : initialKind === "work" ? "Työvaiheet kentälle" : initialTabProp === "materials" ? "Vakiotarvikkeet" : "Mitä kenttähenkilö täyttää")
                : `${report.units.length} yksikköä`}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="manage-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {mode === "all" && (
          <div className="flex border-b border-slate-100">
            {tabBtn("units", "Yksiköt", ListPlus)}
            {tabBtn("checklist", "Tarkistuslista", CheckSquare)}
            {tabBtn("materials", "Tarvikkeet", Package)}
          </div>
        )}
        {mode === "content" && !initialKind && !initialTabProp && (
          <div className="flex border-b border-slate-100">
            {tabBtn("checklist", "Työvaiheet & Havainnot", CheckSquare)}
            {tabBtn("materials", "Vakio-tarvikkeet", Package)}
          </div>
        )}

        <div className="p-5 space-y-5 overflow-y-auto">
          {tab === "units" && (
            <>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lisää yksittäinen</label>
                <div className="flex gap-2">
                  <input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOne())}
                    placeholder="esim. Lämmönjakohuone"
                    className="input-base flex-1"
                    data-testid="manage-new-label"
                  />
                  <button onClick={addOne} disabled={busy || !newLabel.trim()} className="btn-primary px-4 disabled:opacity-50" data-testid="manage-add-one">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lisää useita (yksi per rivi)</label>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={4}
                  className="input-base font-mono text-sm"
                  placeholder="Piippu 5&#10;Piippu 6&#10;Saunaosasto"
                  data-testid="manage-bulk-text"
                />
                <button onClick={addBulk} disabled={busy || !bulkText.trim()} className="btn-ghost mt-2 text-sm disabled:opacity-50" data-testid="manage-bulk-add">
                  <ListPlus className="h-4 w-4" /> Lisää kaikki
                </button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-700">Nykyiset yksiköt</div>
                  <div className="hidden md:block text-xs text-slate-400 italic">Järjestys ↑↓</div>
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {report.units.map((u, idx) => (
                    <UnitRow
                      key={u.id}
                      unit={u}
                      idx={idx}
                      total={report.units.length}
                      busy={busy}
                      onRename={async (newLabel) => {
                        setBusy(true);
                        try {
                          const { data } = await api.patch(`/reports/${report.report_id}/units/${u.id}`, { label: newLabel });
                          onChanged(data);
                          toast.success("Yksikkö nimetty uudelleen");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Uudelleennimeäminen epäonnistui");
                        } finally { setBusy(false); }
                      }}
                      onMoveUp={() => moveUnit(u.id, "up")}
                      onMoveDown={() => moveUnit(u.id, "down")}
                      onRemove={() => removeUnit(u.id)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {tab === "checklist" && (
            <>
              <div className={`rounded-xl border p-3 text-sm ${clKind === "findings" ? "bg-amber-50 border-amber-100 text-amber-900" : "bg-emerald-50 border-emerald-100 text-emerald-900"}`}>
                {clKind === "findings"
                  ? "Lisää havaintochecklist. Kenttähenkilö ruksaa nämä silloin kun havaitsee poikkeavuuden."
                  : "Lisää työvaiheita. Kenttähenkilö ruksaa nämä sitä mukaa kun ne suoritetaan."}
              </div>

              {/* Kind selector — piilotettu jos kutsutaan initialKind-propilla */}
              {!kindLocked && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setClKind("normal")}
                    className={`px-3 py-2 rounded-lg border text-left text-xs transition-colors ${
                      clKind === "normal" ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-500"
                    }`}
                    data-testid="broadcast-kind-normal"
                  >
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      <CheckSquare className="h-3.5 w-3.5" /> Normaali
                    </div>
                    <div className="mt-0.5">Tavalliset tarkistuskohdat.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setClKind("findings")}
                    className={`px-3 py-2 rounded-lg border text-left text-xs transition-colors ${
                      clKind === "findings" ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-500"
                    }`}
                    data-testid="broadcast-kind-findings"
                  >
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" /> Puute-checklist
                    </div>
                    <div className="mt-0.5">Ruksatut kohdat → &quot;Havaitut puutteet&quot;.</div>
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Kohdat (yksi per rivi)</label>
                <textarea
                  value={clText}
                  onChange={(e) => setClText(e.target.value)}
                  rows={5}
                  className="input-base font-mono text-sm"
                  placeholder={clKind === "findings" ? "Vesivuoto havaittu&#10;Puhallin vaihtokunnossa&#10;Suodatin tukossa" : "Kuvat otettu&#10;Saumat tarkistettu&#10;Jätteet siivottu"}
                  data-testid="manage-checklist-text"
                />
              </div>

              {/* Unit selector — user can choose which units receive the items */}
              {report.units.length > 0 && (
                <div data-testid="manage-checklist-units">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold text-slate-700">Lisää yksiköille</label>
                    <button
                      type="button"
                      onClick={toggleAllUnits}
                      className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                      data-testid="manage-checklist-units-toggle-all"
                    >
                      {clSelectedUnits.size === report.units.length ? "Tyhjennä valinta" : "Valitse kaikki"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto p-1 rounded-lg bg-slate-50/50 border border-slate-100">
                    {report.units.map((u) => {
                      const checked = clSelectedUnits.has(u.id);
                      return (
                        <label
                          key={u.id}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm cursor-pointer transition-colors ${
                            checked ? "bg-emerald-100 text-emerald-900" : "bg-white text-slate-600 hover:bg-slate-100"
                          }`}
                          data-testid={`manage-checklist-unit-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleUnit(u.id)}
                            className="h-4 w-4 accent-emerald-500"
                          />
                          <span className="truncate">{u.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <button
                  onClick={broadcastChecklist}
                  disabled={busy || !clText.trim() || report.units.length === 0 || clSelectedUnits.size === 0}
                  className={`text-sm disabled:opacity-50 ${clKind === "findings" ? "btn-primary bg-amber-600 hover:bg-amber-700" : "btn-primary"}`}
                  data-testid="manage-checklist-broadcast"
                >
                  <Plus className="h-4 w-4" /> {clSelectedUnits.size === report.units.length ? "Lisää kaikille yksiköille" : `Lisää valituille (${clSelectedUnits.size})`}
                </button>
              </div>
              <div className="text-xs text-slate-500 italic">
                Vinkki: Lisää usein tarvitut kohdat <strong>Asetukset → Yrityksen checklistit</strong> (valitse tyyppi: normaali tai puute), niin saat ne mukaan uusiin raportteihin automaattisesti.
              </div>
            </>
          )}

          {tab === "devices" && (
            <FieldsTab
              busy={busy}
              setBusy={setBusy}
              report={report}
              onChanged={onChanged}
              fieldKey="device_fields"
              withUnit={false}
              title="Laitetiedot"
              hint="Määrittele mitä laitetietoja kenttähenkilö täyttää yksikölle (esim. Valmistaja, Malli, Sarjanumero, Käyttövuosi)."
              addLabelPlaceholder="Kentän nimi (esim. Sarjanumero)"
              addUnitPlaceholder=""
            />
          )}

          {tab === "measurements" && (
            <FieldsTab
              busy={busy}
              setBusy={setBusy}
              report={report}
              onChanged={onChanged}
              fieldKey="measurement_fields"
              withUnit={true}
              title="Mittaustulokset"
              hint="Määrittele mitä mittauksia yksikkötasolla kerätään (esim. Ilmamäärä l/s, Paine Pa, Lämpötila °C)."
              addLabelPlaceholder="Mittauksen nimi"
              addUnitPlaceholder="Yksikkö"
              unitSuggestions={["l/s", "m³/h", "Pa", "°C", "%", "kPa", "dB"]}
            />
          )}

          {tab === "materials" && (
            <>
              <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900">
                Lisää tarvikkeita raportille jälkikäteen. Tarvikkeet näkyvät pikavalintoina yksiköiden materiaalilistalla.
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Uusi tarvike</label>
                <div className="grid grid-cols-[1fr_80px_auto] gap-2">
                  <input
                    value={matName}
                    onChange={(e) => setMatName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), appendMaterial())}
                    placeholder="esim. Silikoni"
                    className="input-base"
                    data-testid="manage-material-name"
                  />
                  <input
                    value={matUnit}
                    onChange={(e) => setMatUnit(e.target.value)}
                    placeholder="kpl"
                    className="input-base"
                    data-testid="manage-material-unit"
                  />
                  <button
                    onClick={appendMaterial}
                    disabled={busy || !matName.trim()}
                    className="btn-primary px-4 disabled:opacity-50"
                    data-testid="manage-material-add"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">Nykyiset tarvikkeet</div>
                {(report.materials || []).length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Ei vielä tarvikkeita</div>
                ) : (
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {(report.materials || []).map((m, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-100" data-testid={`manage-material-row-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{m.name}</div>
                          <div className="text-xs text-slate-500">{m.unit}</div>
                        </div>
                        <button
                          onClick={() => removeMaterial(m.name)}
                          disabled={busy}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                          data-testid={`manage-material-remove-${i}`}
                          title="Poista tarvike"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer "Valmis" -painike poistettu — käyttäjä sulkee headerin X:llä.
            Tämä estää tilanteen jossa käyttäjä lisää checklist-kohdan tekstikenttään,
            painaa Valmis:ia (joka vain sulki dialogin) ja kohta hävisi tallentamatta.
            Ensisijainen toiminto (esim. "Lisää kaikille yksiköille") on jo inline. */}
      </div>
    </div>
  );
}

function UnitRow({ unit, idx, total, busy, onRename, onMoveUp, onMoveDown, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(unit.label);

  const commit = () => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === unit.label) {
      setValue(unit.label);
      setEditing(false);
      return;
    }
    onRename(trimmed);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100" data-testid={`manage-row-${unit.id}`}>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setValue(unit.label); setEditing(false); }
            }}
            className="w-full px-2 py-1 text-sm font-semibold text-slate-900 rounded-md border border-emerald-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none bg-white"
            data-testid={`manage-rename-input-${unit.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="w-full text-left text-sm font-semibold text-slate-900 truncate hover:text-emerald-700 inline-flex items-center gap-1.5"
            data-testid={`manage-rename-btn-${unit.id}`}
            title="Klikkaa nimetäksesi uudelleen"
          >
            {unit.label}
            <Pencil className="h-3 w-3 text-slate-300" />
          </button>
        )}
      </div>
      {editing ? (
        <button
          onClick={commit}
          className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
          data-testid={`manage-rename-confirm-${unit.id}`}
          title="Tallenna"
        >
          <CheckIcon className="h-4 w-4" />
        </button>
      ) : (
        <>
          <div className="hidden md:flex items-center gap-0.5">
            <button
              onClick={onMoveUp}
              disabled={busy || idx === 0}
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid={`manage-move-up-${unit.id}`}
              title="Siirrä ylös"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={busy || idx === total - 1}
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid={`manage-move-down-${unit.id}`}
              title="Siirrä alas"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={onRemove}
            disabled={busy}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
            data-testid={`manage-remove-${unit.id}`}
            title="Poista yksikkö"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}


/**
 * FieldsTab — hallitsee raportin `measurement_fields` tai `device_fields` -listaa.
 * Näytetään ManageUnitsDialogin content-modessa. Muutokset tallennetaan
 * PATCH /reports/{id} -kutsulla (koko lista lähetetään uudestaan).
 */
function FieldsTab({
  busy, setBusy, report, onChanged, fieldKey, withUnit,
  title, hint, addLabelPlaceholder, addUnitPlaceholder, unitSuggestions = [],
}) {
  const fields = report[fieldKey] || [];
  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("");

  const persist = async (nextFields) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/reports/${report.report_id}`, {
        [fieldKey]: nextFields,
      });
      onChanged(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const label = newLabel.trim();
    if (!label) return;
    const entry = withUnit ? { label, unit: newUnit.trim() } : { label };
    const next = [...fields.map((f) => ({ ...f })), entry];
    persist(next);
    setNewLabel("");
    setNewUnit("");
  };

  const remove = (idx) => {
    const next = fields.filter((_, i) => i !== idx);
    persist(next);
  };

  return (
    <>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-1">{title}</div>
        <p className="text-xs text-slate-500 mb-3">{hint}</p>

        {fields.length > 0 ? (
          <div className="space-y-1.5" data-testid={`manage-${fieldKey}-list`}>
            {fields.map((f, i) => (
              <div
                key={f.id || i}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100"
                data-testid={`manage-${fieldKey}-row-${i}`}
              >
                <span className="flex-1 text-sm font-semibold text-slate-900 truncate">{f.label}</span>
                {withUnit && f.unit && (
                  <span className="text-xs text-slate-600 font-medium px-2 py-0.5 rounded-full bg-slate-100">{f.unit}</span>
                )}
                <button
                  onClick={() => remove(i)}
                  disabled={busy}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                  data-testid={`manage-${fieldKey}-remove-${i}`}
                  title="Poista kenttä"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic mb-3">Ei vielä kenttiä.</div>
        )}
      </div>

      <div className="pt-4 border-t border-slate-100">
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lisää uusi kenttä</label>
        <div className={withUnit ? "grid grid-cols-[1fr_90px_auto] gap-2" : "flex gap-2"}>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder={addLabelPlaceholder}
            className="input-base"
            data-testid={`manage-${fieldKey}-new-label`}
          />
          {withUnit && (
            <input
              value={newUnit}
              onChange={(e) => setNewUnit(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder={addUnitPlaceholder}
              className="input-base"
              list={`manage-${fieldKey}-units`}
              data-testid={`manage-${fieldKey}-new-unit`}
            />
          )}
          <button
            onClick={add}
            disabled={busy || !newLabel.trim()}
            className="btn-primary px-4 disabled:opacity-50"
            data-testid={`manage-${fieldKey}-add`}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        {withUnit && unitSuggestions.length > 0 && (
          <>
            <datalist id={`manage-${fieldKey}-units`}>
              {unitSuggestions.map((u) => <option key={u} value={u} />)}
            </datalist>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {unitSuggestions.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setNewUnit(u)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                    newUnit === u ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

